import { Box, Button, Grid, InputLabel, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Stack } from '@mui/system'
import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'
import get from 'lodash.get'
import React, { FormEvent, useContext, useEffect, useState } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'
import { AuthContext } from '../../../context/auth'
import { FormContext } from '../../../context/form'

import { PDFIcon, TrashIcon } from '../../icons/icons'
import { ErrorOutline } from '@mui/icons-material'



interface FileState {
    id: number
    name: string
    size: number
    loading: boolean
    error: boolean
    file: File
}

function bytesToMegabytes(bytes: number): number {
    const megabytes = bytes / (1024 * 1024)
    return megabytes
}

function bytesToKBorMB(bytes: number): string {
    const KB = 1024
    const MB = 1024 * KB

    if (bytes < MB) {
        const KBValue = bytes / KB
        return `${KBValue.toFixed(1)}KB`
    } else {
        const MBValue = bytes / MB
        return `${MBValue.toFixed(1)}MB`
    }
}

export default function DropFileUpload({
    name = 'filesUid',
    tipoArquivo,
    title,
    required = false,
    multiple = false,
    apiURL,
    sizeLimit = 4,
    xs = 12,
    sm,
    md,
    route = '',
    tstToken = '',
    dropZoneOptions,
    disabled = false,
}: {
    name?: string
    tipoArquivo: string
    title: string
    apiURL: string
    route?: string
    tstToken?: string
    required?: boolean
    multiple?: boolean
    sizeLimit?: number
    dropZoneOptions?: DropzoneOptions
    xs?: number
    sm?: number
    md?: number
    disabled?: boolean
}) {
    const { getRootProps, getInputProps, inputRef } = useDropzone({
        disabled,
        multiple,
        useFsAccessApi: true,
        onDrop: (dropFiles) => {
            const fileList: FileState[] = []
            setProgress(-1)

            dropFiles
                .filter((file) => {
                    if (bytesToMegabytes(file.size) > sizeLimit) {
                        setErrorMsg(`Por favor, escolha um arquivo com tamanho inferior a ${sizeLimit} MB`)

                        setTimeout(() => {
                            setErrorMsg('')
                        }, 3000)

                        return false
                    }

                    return true
                })
                .forEach((file, index) => {
                    let id: number = Date.now() + index

                    // fetch API

                    const fd = new FormData()

                    fd.append('files', file)
                    fd.append('tipoArquivo', tipoArquivo)

                    axios
                        .post(apiURL, fd, {
                            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!)
                                setProgress(percentCompleted)

                                if (percentCompleted >= 100) {
                                    setFiles((f) => [...f, { id: id, name: file.name, loading: true, error: false, file: file, size: file.size }])
                                }
                            },
                            headers: { Authorization: `Bearer ${tstToken === '' ? (user ? user.token : '') : tstToken}` },
                        })
                        .then((res: AxiosResponse<any, any>) => {
                            if (res.status > 200) {
                                const fileIdFromApi = get(res.data, route, res.data)[0]
                                const fileId: number = fileIdFromApi['coSeqArquivo']

                                context.setFilesUid((fId) => [
                                    ...fId,
                                    {
                                        CO_SEQ_ARQUIVO: fileId,
                                        CO_TIPO_ARQUIVO: parseInt(tipoArquivo),
                                    },
                                ])
                                setFilesLoaded((fl) => [...fl, id])

                                const f: { [key: number]: number } = {}
                                f[id] = fileId
                                setFilesIds((ids) => ({ ...ids, ...f }))
                            } else {
                                setProgress(-1)
                                removeFile(id)
                            }
                        })
                        .catch((err) => {
                            console.log(err)
                            setProgress(-1)
                            removeFile(id)
                        })
                })
        },
        ...(dropZoneOptions || {}),
    })
    const assignRef = <T,>(...refs: Array<React.Ref<T> | undefined>) => {
        return (node: T | null) => {
            refs.forEach((ref) => {
                if (!ref) return

                if (typeof ref === 'function') {
                    ref(node)
                    return
                }

                ;(ref as React.MutableRefObject<T | null>).current = node
            })
        }
    }
    const context = useContext(FormContext)!
    const { user } = useContext(AuthContext)

    const theme = useTheme()
    const isSmall = useMediaQuery(theme.breakpoints.only('xs'))

    const [progress, setProgress] = useState(-1)
    const [files, setFiles] = useState<FileState[]>([])
    const [filesLoaded, setFilesLoaded] = useState<number[]>([])
    const [fileIds, setFilesIds] = useState<{ [key: number]: number }>({})
    // const [filesError, setFilesError] = useState<number[]>([])

    const [errorMsg, setErrorMsg] = useState('')

    const removeFile = (id: number, hideMsg?: boolean, fileId?: number) => {
        setFiles(files.filter((x) => x.id !== id))

        if (fileId) context.setFilesUid((fId) => fId.filter((idd) => idd.CO_SEQ_ARQUIVO !== fileId))

        if (!hideMsg) {
            setErrorMsg('Erro ao enviar arquivo. Verifique o formato e tente mais tarde.')

            setTimeout(() => {
                setErrorMsg('')
            }, 3000)
        }
    }

    const deleteFile = (e: FormEvent, id: number) => {
        if (Object.keys(fileIds).includes(id.toString())) {
            fetch(`${apiURL}/${fileIds[id]}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${tstToken === '' ? (user ? user.token : '') : tstToken}`,
                },
            })
                .then((res) => {
                    if (!res.ok) removeFile(id, true, fileIds[id])

                    if (res.status === 200) {
                        removeFile(id, true, fileIds[id])
                    }
                })
                .catch((err) => console.log(err))
        }
    }

    useEffect(() => {
        if (apiURL) {
            const uploadedFiles = files
                .filter(f => fileIds[f.id] !== undefined)
                .map(f => ({
                    CO_SEQ_ARQUIVO: fileIds[f.id],
                    CO_TIPO_ARQUIVO: parseInt(tipoArquivo),
                }))

            context?.formSetValue(name!, uploadedFiles)
        } else {
            const dt = new DataTransfer()
            files.forEach((x) => {
                dt.items.add(x.file)
            })
            context?.formSetValue(name!, dt.files)
        }
    }, [files, context, name, fileIds, apiURL, tipoArquivo])

    useEffect(() => {
        return () => {
            context.setFilesUid((files) => files.filter((x) => x.CO_TIPO_ARQUIVO !== parseInt(tipoArquivo)))
        }
    }, [])

    return (
        <Grid item {...{ xs, sm, md }} sx={{ width: '100%', opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            <Box bgcolor='white' p={2} borderRadius='8px' color='#1E293B'>
                <InputLabel required={required} sx={{ marginBottom: 2, textTransform: 'capitalize' }}>
                    {title}
                </InputLabel>
                <Stack
                    {...getRootProps({ className: 'dropzone' })}
                    bgcolor='#EFEFEF'
                    justifyContent='center'
                    alignItems='center'
                    textAlign='center'
                    borderRadius='6px'
                    py={8}
                    border='solid 1.5px #989898'
                    sx={{
                        borderStyle: 'dashed',
                        cursor: 'pointer',
                    }}
                >
                    {(() => {
                        const inputProps = getInputProps()
                        const { ref: formRef } = context.formRegister(name, {
                            validate: (v, f) => {
                                if (required && (v === null || v === undefined || (Array.isArray(v) && v.length === 0))) {
                                    return 'O campo de arquivo é obrigatório'
                                }
                            },
                        })

                        return <input {...inputProps} ref={assignRef(inputRef, formRef)} />
                    })()}
                    <Stack spacing={2} alignItems='center'>
                        <Box>
                            <Typography fontWeight={600} fontSize={18}>
                                Arraste seus arquivos até aqui
                            </Typography>
                            <Typography>ou selecione arquivos que estão no seu computador</Typography>
                        </Box>
                        <Button
                            variant='contained'
                            sx={{
                                backgroundColor: '#64748B',
                                pointerEvents: 'none',
                                borderRadius: '8px',
                                width: 'fit-content',
                            }}
                            disabled={disabled}
                        >
                            Selecionar
                        </Button>
                        <Typography fontWeight={300}>Tamanho máximo por arquivo {sizeLimit}MB</Typography>
                    </Stack>
                </Stack>

                <Typography pt={2} fontSize={16} fontWeight={600}>
                    Você selecionou {files.length} arquivo{files.length > 1 ? 's' : ''}.
                </Typography>

                <Stack width='100%' marginTop={1} spacing={1}>
                    {files.map((x) => (
                        <Stack direction='row' justifyContent='space-between' border='solid 1px #E2E8F0' borderRadius={2} p={1}>
                            <Stack direction='row'>
                                <Stack direction='row' justifyContent='center' alignItems='center' minWidth={30} pr={1.5}>
                                    <PDFIcon
                                        sx={{
                                            filter: 'invert(42%) sepia(86%) saturate(2412%) hue-rotate(326deg) brightness(86%) contrast(102%)',
                                            transform: 'scale(1.5)',
                                            width: 30,
                                        }}
                                    />
                                </Stack>
                                <Stack>
                                    <Typography fontWeight={600}>{x.name}</Typography>
                                    <Typography fontSize={14}>{bytesToKBorMB(x.size)}</Typography>
                                </Stack>
                            </Stack>

                            <Button
                                size='small'
                                startIcon={<TrashIcon />}
                                variant='contained'
                                onClick={(e) => deleteFile(e, x.id)}
                                sx={{
                                    height: 40,
                                    backgroundColor: '#DE3F50',
                                    borderRadius: '8px',
                                }}
                                disabled={disabled}
                            >
                                Remover
                            </Button>
                        </Stack>
                    ))}

                    {progress > 0 && progress < 100 && (
                        <LinearProgress
                            value={progress}
                            sx={{
                                backgroundColor: '#103D6A',
                                '.MuiLinearProgress-bar': {
                                    backgroundColor: '#BDDDFA',
                                },
                            }}
                        />
                    )}
                </Stack>

                {errorMsg && (
                    <Box
                        sx={{
                            backgroundColor: '#FFEBEE',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginTop: '16px',
                            border: '1px solid #FFCDD2',
                            color: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <ErrorOutline fontSize='small' />
                        <Typography variant='caption' color='inherit' fontWeight={600} fontSize={14}>
                            {errorMsg}
                        </Typography>
                    </Box>
                )}

                {get(context?.errors, name!) && (
                    <Box
                        sx={{
                            backgroundColor: '#FFEBEE',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            marginTop: '8px',
                            border: '1px solid #FFCDD2',
                            color: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <ErrorOutline fontSize='small' />
                        <Typography variant='caption' color='inherit' fontWeight={600} fontSize={14}>
                            O campo de arquivo é obrigatório
                        </Typography>
                    </Box>
                )}
            </Box>
        </Grid>
    )
}



